import React, { useState, useEffect } from 'react';
import { FaPlus, FaChevronCircleLeft, FaChevronCircleRight } from 'react-icons/fa';
import AddSection from './AddSection';
import { useSelector, useDispatch } from 'react-redux';
import { fetchSections, fetchGroups } from '../../../features/sections/sectionsAPI';
import { selectSections, updateSections } from '../../../features/sections/sectionSlice';
import LoadingSpinner from '../../LoadingSpinner';

const Sections = () => {
    const [loading, setLoading] = useState(true);
    const sections = useSelector(selectSections);
    const dispatch = useDispatch();
    const [showAddSection, setShowAddSection] = useState(false);
    const [token, setToken] = useState('');
    const [sectionToEdit, setSectionToEdit] = useState(null);
    const [groups, setGroups] = useState([]);
    const [currentGroupIndex, setCurrentGroupIndex] = useState(0);
    const [forceUpdate, setForceUpdate] = useState(false); // State to trigger re-render

    useEffect(() => {
        const fetchData = async () => {
            try {
                const data = await fetchSections(token);
                dispatch(updateSections(data));
                setLoading(false); // Set loading to false after updating sections
            } catch (error) {
                console.error('Error fetching sections:', error);
            }
        };

        fetchData();
    }, [token, dispatch, forceUpdate]); // Include forceUpdate in dependencies array

    useEffect(() => {
        const storedToken = localStorage.getItem('token');
        if (storedToken) {
            setToken(storedToken);
            fetchAllGroups(storedToken); // Fetch groups when token is set
        }
    }, [forceUpdate]);

    const fetchAllGroups = async (token) => {
        try {
            const allGroups = await fetchGroups(token);
            setGroups(allGroups);
            setLoading(false);
        } catch (error) {
            console.error('Error fetching groups:', error);
        }
    };

    const handleAddSectionClick = () => {
        setShowAddSection(true);
        setSectionToEdit(null);
    };

    const handleCloseAddSection = () => {
        setShowAddSection(false);
        setSectionToEdit(null);
    };

    const handleSectionClick = (section) => {
        setSectionToEdit(section);
        setShowAddSection(true);
    };

    const capitalizeFirstLetter = (string) => {
        return string.charAt(0).toUpperCase() + string.slice(1);
    };

    const navigateGroups = (direction) => {
        if (direction === 'next') {
            setCurrentGroupIndex((prevIndex) => (prevIndex + 1) % groups.length);
        } else if (direction === 'prev') {
            setCurrentGroupIndex((prevIndex) => (prevIndex - 1 + groups.length) % groups.length);
        }
    };

    if (loading) {
        return <LoadingSpinner />;
    }

    return (
        <div className="sections-container bg-white p-6 rounded-md w-full" style={{ height: '100vh', overflowY: 'auto' }}>
            <h2 className="sections-title text-2xl font-bold mb-4 text-center">Sections</h2>
            <div className="groups-container px-4">
                {groups.map((group, index) => (
                    <div key={index} className="group-section" style={{ display: index === currentGroupIndex ? 'block' : 'none' }}>
                        {/* Group name & icons to switch */}
                        <div className='flex w-full justify-between my-4 mx-2'>
                            <FaChevronCircleLeft
                                className="cursor-pointer w-8 h-8 text-gray-500"
                                onClick={() => navigateGroups('prev')}
                            />
                            <h2 className="group-title text-xl font-bold text-center mb-2">{capitalizeFirstLetter(group)}</h2>
                            <FaChevronCircleRight
                                className="cursor-pointer w-8 h-8 text-gray-500 "
                                onClick={() => navigateGroups('next')}
                            />
                        </div>
                        <div className='flex justify-center flex-wrap gap-2'>
                            {sections
                                .filter((section) => section.group.toLowerCase() === group.toLowerCase())
                                .map((section, index) => (
                                    <div
                                        key={index}
                                        className={`cursor-pointer bg-white p-4 lg:w-1/2 w-full rounded-md shadow-lg ml-4 lg:ml-0 mb-2 border-l-4`}
                                        style={{ borderColor: `${section.color}` }}
                                        onClick={() => handleSectionClick(section)}
                                    >
                                        <h4 className="section-day text-base font-medium mb-1">{section.days}</h4>
                                        <p className="section-time text-sm">{section.time_period}</p>
                                    </div>
                                ))}
                        </div>
                    </div>
                ))}
            </div>
            <div className="flex justify-center items-center">
                <button
                    onClick={handleAddSectionClick}
                    className="fixed text-white rounded-full p-3 bg-gray-500 hover:bg-gray-600"
                    style={{ left: '50%', bottom: '5%', transform: 'translateX(-50%)', zIndex: '40' }}
                >
                    <FaPlus />
                </button>
            </div>
            {showAddSection && (
                <AddSection token={token} onClose={handleCloseAddSection} sectionInfo={sectionToEdit} onSectionUpdate={() => setForceUpdate(!forceUpdate)} />
            )}
        </div>
    );
};

export default Sections;
